import React, { useState } from 'react'
import {
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  CircularProgress,
  IconButton,
  ImageList,
  ImageListItem,
  ImageListItemBar,
  TextareaAutosize,
} from '@mui/material'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import DeleteIcon from '@mui/icons-material/Delete'
import StopIcon from '@mui/icons-material/Stop'
import KeyboardVoiceIcon from '@mui/icons-material/KeyboardVoice'
import useVoskDictation from '../../util/useVoskDictation'

export default function ClinicEditor({
  classes,
  rest,
  comment,
  onCommentChange,
  title,
  onTitleChange,
  onFileChange,
  imageError,
  onUpload,
  images = [],
  imageLoading,
  onDeleteImage,
  isAdmin,
}) {
  const [fileCount, setFileCount] = useState(0)

  const {
    isTranscribing,
    transcriptionError,
    startDictation,
    stopDictation,
  } = useVoskDictation()

  const applySmartDictationFormatting = (text) => {
    let formatted = String(text || '')
    formatted = formatted.replace(/\bi\b/g, 'I')
    formatted = formatted.replace(/(^\s*[a-z])|([.!?]\s+[a-z])/g, (segment) => segment.toUpperCase())
    return formatted
  }

  const handleStartDictation = () => {
    if (isTranscribing) return
    startDictation('comment', String(comment || ''), (text) => {
      onCommentChange(applySmartDictationFormatting(text))
    })
  }

  const handleStopDictation = () => {
    stopDictation()
  }

  const handleFileChange = (e) => {
    setFileCount(e.target.files.length)
    onFileChange(e)
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
        padding: '8px 16px',
        minHeight: '60vh',
      }}
    >
      <div style={{ flex: '0 0 auto', minHeight: 0, display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'stretch' }}>
        <div style={{ flex: '0 0 220px', minWidth: 270 }}>
          <Card {...rest} className={classes.card}>
            <CardContent style={{ paddingTop: 10, paddingBottom: 10 }}>
              <Typography variant="h6" className={classes.uploadTitle}>
                Image Upload:
              </Typography>
              <div style={{ height: 5 }} />
              <TextField
                className={classes.ImageTitle}
                variant="outlined"
                id="title"
                label={fileCount > 1 ? 'Title Prefix' : 'Image Title'}
                helperText={fileCount > 1 ? 'Used as "Prefix 1", "Prefix 2" (leave empty to use file names)' : ''}
                name="title"
                value={title}
                onChange={(e) => onTitleChange(e.target.value)}
              />
              <div style={{ height: 6 }} />
              <input
                className={classes.fileUpload}
                id="file-upload"
                type="file"
                multiple
                onChange={handleFileChange}
              />
              {fileCount > 0 && (
                <Typography variant="body2" style={{ marginTop: 4 }}>
                  {fileCount} file{fileCount > 1 ? 's' : ''} selected
                </Typography>
              )}
              {imageError ? (
                <Typography variant="h6" className={classes.error}>
                  {imageError}
                </Typography>
              ) : null}
              <div style={{ height: 5 }} />
              <Button
                variant="outlined"
                color="primary"
                type="submit"
                size="small"
                startIcon={<CloudUploadIcon />}
                className={classes.uploadButton}
                onClick={onUpload}
              >
                Upload Photo
              </Button>
            </CardContent>
          </Card>
        </div>

        <div style={{ flex: '1 1 620px', minWidth: 0 }}>
          <Typography variant="h6" className={classes.uploadTitle}>
            Clinical Info
          </Typography>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
            <form className={classes.form} noValidate style={{ margin: 0, flex: '1 1 460px' }}>
              <TextareaAutosize
                style={{ width: '100%', minWidth: 280, fontSize: '14pt', boxSizing: 'border-box' }}
                maxRows={8}
                minRows={8}
                aria-label="comment"
                name="comment"
                value={comment}
                onChange={(e) => onCommentChange(e.target.value)}
              />
            </form>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 148, maxWidth: 170 }}>
              <Typography variant="caption" color="textSecondary">
                Voice to text
              </Typography>
              <Button
                variant="outlined"
                size="small"
                startIcon={<KeyboardVoiceIcon />}
                onClick={handleStartDictation}
                disabled={isTranscribing}
              >
                {isTranscribing ? 'Listening (Clinical)' : 'Dictate Clinical'}
              </Button>
              <Button
                variant="outlined"
                size="small"
                startIcon={<StopIcon />}
                onClick={handleStopDictation}
                disabled={!isTranscribing}
              >
                Stop
              </Button>
            </div>
          </div>
          {transcriptionError && (
            <Typography variant="body2" color="error" style={{ marginTop: 6 }}>
              {transcriptionError}
            </Typography>
          )}
        </div>
      </div>

      <div
        style={{
          flex: '1 1 640px',
          minHeight: 'clamp(240px, 60vh, 640px)',
          minWidth: 0,
          overflowY: 'auto',
          width: '75vw',
          maxWidth: '100%',
          alignSelf: 'center',
        }}
      >
        {imageLoading && (
          <CircularProgress size={50} className={classes.uiProgess} />
        )}
        {images.length > 0 && (
          <ImageList cols={1} gap={16} style={{ margin: 0, width: '75%' }}>
            {images.map((image) => (
              <ImageListItem key={image.id} cols={1}>
                <img src={image.imageUrl} alt={image.title} style={{ display: 'block', width: '100%', height: 'auto', objectFit: 'contain' }} />
                <ImageListItemBar
                  title={image.title}
                  actionIcon={
                    isAdmin ? (
                      <IconButton
                        sx={{ color: 'rgba(255, 255, 255, 0.54)' }}
                        onClick={() => onDeleteImage(image.id)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    ) : null
                  }
                />
              </ImageListItem>
            ))}
          </ImageList>
        )}
      </div>
    </div>
  )
}